import React, { useState, useEffect } from "react";
import ActionCellRender from "../../components/actionCellRender";
import { IconPencil } from "@tabler/icons";
import { Text, Group } from "@mantine/core";
import {
  PageList,
  ENUM_FORM_DISPLAY,
  ENUM_FORM_LAYOUT_CONTAINER,
} from "../../components/pageList";
import { useCellRender } from "../../hooks/useCellRender";
import FileList from "../../components/fileList";
import ActivityLog from "../../components/activityLog.js";
import OrderApplicantDetail from "../../components/orderApplicantDetail";
import OrderSummary from "../../components/orderSummary";
import OrderServiceSummary from "../../components/orderServiceSummary";
import OrderPolicy from "../../components/orderPolicy";
import OrderPaymentDetail from "../../components/orderPaymentDetail";
import OrderProductDetail from "../../components/orderProductDetail";
import OrderServiceActionList from "../../components/orderServiceActionList";
import ServiceTypeFieldsDisplay from "../../components/orderService/ServiceTypeFieldsDisplay";
import { useAuthUser } from "react-auth-kit";
import RemarkList from "../../components/remarkList";
import { useServerApi } from "../../hooks/userServerApi";
import OrderTimeline from "../../components/orderTimeline";
import {
  applyTeamUserFilters,
  CONSULTANT_TEAM_SEARCH_ENABLED,
  getTeamUserIds,
  isConsultantPortalRole,
} from "../../helpers/consultantTeamFilter";
import { McaApprovalListBadge } from "../../components/mca/McaEntityIndicators";

const apiEntity = "orderService";

export const editFormSchema = {
  display: {
    mode: ENUM_FORM_DISPLAY.LINK,
  },
  title: "Order Service",
  titleField: "code",
  apiEntity,
  initialValues: {},

  layout: {
    containers: [
      {
        key: "main-g",
        parent: null,
        type: ENUM_FORM_LAYOUT_CONTAINER.GRID,
        props: {
          gutter: "xl",
          columns: 24,
        },
        cols: [
          {
            key: "main-g1",
            props: {
              span: 7,
            },
          },
          {
            key: "main-g2",
            props: {
              span: 17,
            },
          },
        ],
      },
      {
        key: "section-applicant",
        parent: "main-g1",
        type: ENUM_FORM_LAYOUT_CONTAINER.SECTION,
        props: {
          title: "Applicant Details",
          withBorder: false,
          mt: 0,
          mb: "md",
        },
      },
      {
        key: "section-action-list",
        parent: "main-g1",
        type: ENUM_FORM_LAYOUT_CONTAINER.SECTION,
        props: {
          title: "Actions",
          withBorder: false,
          minHeight: "0px",
          mt: 0,
          mb: 20,
        },
      },
      {
        key: "main-tab",
        parent: "main-g2",
        type: ENUM_FORM_LAYOUT_CONTAINER.TABS,
        props: {
          variant: "default",
          defaultValue: "Overview",
        },
        tab: [
          { key: "tab-overview", label: "Overview" },
          { key: "tab-files", label: "Files" },
          { key: "tab-logs", label: "Logs" },
        ],
      },
      {
        key: "section-service",
        parent: "tab-overview",
        type: ENUM_FORM_LAYOUT_CONTAINER.SECTION,
        props: {
          title: "Order Service",
          titleOrder: 5,
          withBorder: false,
          minHeight: 0,
          mt: 0,
          mb: 20,
        },
      },
      {
        key: "section-service-fields",
        parent: "tab-overview",
        type: ENUM_FORM_LAYOUT_CONTAINER.SECTION,
        props: {
          title: "Service Field Values",
          titleOrder: 5,
          withBorder: false,
          minHeight: 0,
          mt: 0,
          mb: 20,
        },
      },
      {
        key: "section-remarks",
        parent: "tab-overview",
        type: ENUM_FORM_LAYOUT_CONTAINER.SECTION,
        props: {
          title: "Service Remarks",
          titleOrder: 5,
          withBorder: false,
          minHeight: 0,
          mt: 20,
          mb: 20,
        },
      },
      {
        key: "section-summary",
        parent: "tab-overview",
        type: ENUM_FORM_LAYOUT_CONTAINER.SECTION,
        props: {
          title: "Order Summary",
          titleOrder: 5,
          withBorder: false,
          minHeight: 0,
          mt: 0,
          mb: 20,
        },
      },
      {
        key: "section-timeline",
        parent: "tab-overview",
        type: ENUM_FORM_LAYOUT_CONTAINER.SECTION,
        props: {
          title: "Order Timeline",
          titleOrder: 5,
          withBorder: false,
          minHeight: 0,
          mt: 20,
          mb: 20,
        },
      },
      {
        key: "section-policy",
        parent: "tab-overview",
        type: ENUM_FORM_LAYOUT_CONTAINER.SECTION,
        props: {
          title: "Policy Info",
          titleOrder: 5,
          withBorder: false,
          minHeight: 0,
          mt: 20,
          mb: 20,
        },
      },
      {
        key: "section-product",
        parent: "tab-overview",
        type: ENUM_FORM_LAYOUT_CONTAINER.SECTION,
        props: {
          title: "Product",
          titleOrder: 5,
          withBorder: false,
          minHeight: 0,
          mt: 20,
          mb: 20,
        },
      },
      {
        key: "section-payment",
        parent: "tab-overview",
        type: ENUM_FORM_LAYOUT_CONTAINER.SECTION,
        props: {
          title: "Payment",
          titleOrder: 5,
          withBorder: false,
          minHeight: 0,
          mt: 20,
          mb: 20,
        },
      },
      {
        key: "section-log",
        parent: "tab-logs",
        type: ENUM_FORM_LAYOUT_CONTAINER.SECTION,
        props: {
          title: "Order Log",
          titleOrder: 5,
          withBorder: false,
          mt: 20,
          mb: 20,
        },
      },
      {
        key: "section-files",
        parent: "tab-files",
        type: ENUM_FORM_LAYOUT_CONTAINER.SECTION,
        props: {
          title: "Files",
          titleOrder: 5,
          withBorder: false,
          minHeight: "0px",
          mt: 20,
          mb: 20,
        },
      },
    ],

    fields: [
      {
        name: "orderId",
        component: OrderServiceSummary,
        parent: "section-service",
        props: {},
      },
      {
        name: "fieldValues",
        component: ServiceTypeFieldsDisplay,
        parent: "section-service-fields",
        props: {},
      },
      {
        name: "remarks",
        component: RemarkList,
        parent: "section-remarks",
        props: {
          scrollHeight: 100,
        },
      },
      {
        name: "files",
        component: FileList,
        parent: "section-files",
        props: {},
      },
      {
        name: "log",
        component: ActivityLog,
        parent: "section-log",
        props: {},
      },
      {
        name: "orderId.clientSnapShot",
        component: OrderApplicantDetail,
        parent: "section-applicant",
        props: {},
      },
      {
        name: "orderId",
        component: OrderTimeline,
        parent: "section-timeline",
        props: {},
      },
      {
        name: "orderId",
        component: OrderSummary,
        parent: "section-summary",
        props: {},
      },
      {
        name: "orderId",
        component: OrderPolicy,
        parent: "section-policy",
        props: {},
      },
      {
        name: "orderId",
        component: OrderProductDetail,
        parent: "section-product",
        props: {},
      },
      {
        name: "orderId",
        component: OrderPaymentDetail,
        parent: "section-payment",
        props: {},
      },
      {
        name: "actionList",
        component: OrderServiceActionList,
        parent: "section-action-list",
        props: {},
      },
    ],
  },
};

export default function OrderService({ role = "OPERATION" }) {
  const [cellRender] = useCellRender();
  const auth = useAuthUser();
  const [preQuery, setPreQuery] = useState(() =>
    isConsultantPortalRole(role) ? undefined : {},
  );
  const [teamUserIds, setTeamUserIds] = useState(null);
  const [api] = useServerApi();

  const actionListField = editFormSchema.layout.fields.find(
    (f) => f.name === "actionList",
  );

  const reviseSchemaForConsultant = async () => {
    const fallbackOrderQuery = () => ({
      consultants: auth().id,
    });

    try {
      const teamIds = await getTeamUserIds(auth, api);
      setTeamUserIds(teamIds);

      let orderQuery = fallbackOrderQuery();
      if (CONSULTANT_TEAM_SEARCH_ENABLED) {
        orderQuery = { consultants: { $in: teamIds } };
      }

      const data = await api.search({
        apiEntity: "order",
        pageSize: 5000,
        currentPage: 1,
        sort: { by: "createdAt", order: "desc" },
        searchQuery: orderQuery,
        select: "_id",
        byAggregation: false,
      });

      const orderIds = (data?.docs ?? []).map((o) => o._id);
      setPreQuery({ orderId: { $in: orderIds } });
      actionListField.props.role = role;
    } catch (error) {
      console.log("OrderService reviseSchemaForConsultant error", error);
      setTeamUserIds([auth().id]);
      try {
        const data = await api.search({
          apiEntity: "order",
          pageSize: 5000,
          currentPage: 1,
          searchQuery: fallbackOrderQuery(),
          select: "_id",
          byAggregation: false,
        });
        const orderIds = (data?.docs ?? []).map((o) => o._id);
        setPreQuery({ orderId: { $in: orderIds } });
      } catch {
        setPreQuery({ orderId: { $in: [] } });
      }
      actionListField.props.role = role;
    }
  };

  useEffect(() => {
    actionListField.props.role = role;
    if (isConsultantPortalRole(role)) {
      reviseSchemaForConsultant();
      return;
    }
    setTeamUserIds(null);
  }, [role]);

  const tableSchema = {
    showActionColumn: true,
    byAggregation: false,
    preQuery,
    select:
      "code status serviceType serviceTypeSnapShot orderId description createdAt submissionDate receieveDate completeDate searchText mcaPending mcaRequestId",
    columns: (() => {
    const cols = [
      {
        field: "code",
        headerName: "Order Service",
        sortable: true,
        cellRender: (col, data) => (
          <Group spacing={6} noWrap>
            {cellRender.Order.serviceCode(data)}
            <McaApprovalListBadge entity={data} />
          </Group>
        ),
        filterable: true,
        filter: {
          apiEntity: "orderService",
          field: "status",
          label: "Service Status",
          width: 250,
          height: 300,
        },
      },
      {
        field: "order.code",
        headerName: "Order",
        sortable: true,
        cellRender: (col, data) => cellRender.Order.code(data?.orderId ?? data),
      },
      {
        field: "_filterServiceType",
        headerName: "Service Type",
        filterOnly: true,
        filterable: true,
        filter: {
          apiEntity: "orderService",
          field: "serviceType",
          label: "Service Type",
          width: 300,
          height: 320,
          populate: {
            model: "OrderServiceType",
            select: "name cname code",
            labelField: "name",
            sort: { name: 1 },
          },
        },
      },
      {
        field: "_filterOrderConsultants",
        headerName: "Consultants",
        filterOnly: true,
        filterable: true,
        filter: {
          apiEntity: "orderService",
          field: "orderConsultants",
          label: "Consultants",
          width: 300,
          height: 320,
          ...(!isConsultantPortalRole(role)
            ? {
                userPreQuery: { isConsultant: true, status: "ACTIVE" },
              }
            : {}),
          populate: {
            model: "User",
            select: "name cname staffId aliasName",
            labelField: "name",
            sort: { name: 1 },
          },
        },
      },
      {
        field: "_filterOrderProvider",
        headerName: "Insurer",
        filterOnly: true,
        filterable: true,
        filter: {
          apiEntity: "orderService",
          field: "orderProvider",
          label: "Insurer",
          width: 320,
          height: 300,
          populate: {
            model: "Provider",
            select: "name cname code shortName",
            labelField: "name",
            sort: { name: 1 },
          },
        },
      },
      {
        field: "product",
        headerName: "Product",
        sortable: true,
        cellRender: (col, data) => {
          const order = data.orderId;
          if (!order) return null;
          if (order.product) return cellRender.ProductInfo(order.product);
          if (order.productType || order.provider)
            return (
              <Text color="dimmed" size="xs">
                {cellRender.ProductInfo2(order)}
              </Text>
            );
        },
      },
      {
        field: "policyNumber",
        headerName: "Policy No.",
        sortable: true,
        cellRender: (col, data) => <>{data.orderId?.policyNumber}</>,
      },
      {
        field: "client",
        sortable: true,
        cellRender: (col, data) =>
          data?.orderId
            ? cellRender.ClientInfo(data?.orderId?.clientSnapShot, true)
            : cellRender.ClientInfo(data, true),
      },
      {
        field: "consultants",
        headerName: "Consultants",
        sortable: true,
        cellRender: (col, data) =>
          data?.orderId
            ? cellRender.Users(data?.orderId?.consultants, 3, true)
            : [],
      },
      {
        field: "action",
        cellRender: ActionCellRender,
        isAction: true,
        actions: [
          {
            name: "edit",
            icon: IconPencil,
            props: {},
          },
        ],
        actionMenu: [],
      },
    ];

    if (isConsultantPortalRole(role) && teamUserIds?.length) {
      applyTeamUserFilters(cols, teamUserIds, ["orderConsultants"]);
    }

    return cols;
    })(),
    searchableFields: [
      "status",
      "code",
      "serviceTypeSnapShot.name",
      "order.policyNumber",
    ],
  };

  return (
    <>
      {preQuery && (
        <PageList
          title={"Order Services"}
          apiEntity={apiEntity}
          tableSchema={tableSchema}
          formSchema={editFormSchema}
          showImport={true}
          showTemplateDownload={true}
          templateDownloadUrl={`${process.env.REACT_APP_SERVER_URL}/orderService/downloadTemplate`}
          templateDownloadText="Download Template"
          initSort={{ by: "createdAt", order: "desc" }}
          showAddButton={false}
          accessLinkEntity="order"
          searchByTextField={true}
        />
      )}
    </>
  );
}
